// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FormField, { FormFieldComponentProps, FormFieldPresentationProps } from "@eisinc/MobiusOverrides/FormField";
import { HasDisablerContext, withDisabler } from "@insite/mobius/utilities/DisablerContext";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import * as React from "react";

export type TextAreaComponentProps = MobiusStyledComponentProps<
    "textarea",
    {
        /** Error message to be displayed below the textarea. */
        error?: React.ReactNode;
        /** Hint text to be displayed below the textarea. */
        hint?: React.ReactNode;
        /** Label to be displayed above the textarea. */
        label?: React.ReactNode;
    } & Partial<FormFieldComponentProps>
>;

export type TextAreaProps = FormFieldPresentationProps<TextAreaComponentProps> & TextAreaComponentProps;

/**
 * TextArea is a form element with an optional label, hint text, error message and optional icon.
 * Props not contained in the list below get passed into the input component (e.g. event handlers, `value`, etc).
 */
const TextArea: React.FC<TextAreaProps & HasDisablerContext> = props => {
    const { rows = 4, disable, disabled, error, hint, id, placeholder, required, ...otherProps } = props;
    const isDisabled = !!(disable || disabled);
    const inputId = id || uniqueId();
    const labelId = `${inputId}-label`;
    const inputLabelObj = otherProps.label === 0 || otherProps.label ? { "aria-labelledby": labelId } : {};
    const descriptionId = `${inputId}-description`;
    const hasDescription = error || hint;

    const textInput = (
        <>
            <textarea
                id={inputId}
                aria-describedby={hasDescription ? descriptionId : undefined}
                aria-invalid={!!error}
                aria-required={!disabled && required}
                tabIndex={0}
                {...{ disabled: isDisabled, placeholder, required }}
                {...omitMultiple(otherProps, ["sizeVariant", "border", "label", "backgroundColor", "cssOverrides"])}
                {...inputLabelObj}
                rows={rows}
            />
        </>
    );

    return (
        <FormField
            descriptionId={descriptionId}
            formInput={textInput}
            labelId={labelId}
            inputId={inputId}
            disabled={isDisabled}
            {...props}
        />
    );
};

TextArea.displayName = "TextArea";
/** @component */
export default withDisabler(TextArea);
